import { Injectable, inject } from '@angular/core';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';

import { LinkAnchorData } from './operations.service';
import { PortsService } from './ports.service';
import { PositionService } from './position.service';
import { SizeService } from './size.service';
import { ElementClasses } from '../../model/element-classes.model';
import { FlowchartConfig, FontAttributes } from '../base/flowchart.config';

export type CreateActionNodeOptions = {
  id: string;
  uniqueId: string;
  tooltip: string;
  x: number;
  y: number;
  size: { width: number; height: number };
  colorPresetId: string;
  activityType: number;
};
@Injectable({
  providedIn: 'root',
})
export class ActionNodeService {
  private positionService = inject(PositionService);
  private sizeService = inject(SizeService);
  private translocoService = inject(TranslocoService);

  createActionNode(text: string, options?: Partial<CreateActionNodeOptions>): joint.dia.Cell {
    const nodeText = text ?? '';

    const action = new joint.shapes.standard.Rectangle({
      position: { x: options?.x, y: options?.y },
      size: options?.size ? options?.size : FlowchartConfig.nodeSize.default,
      z: 1,
      attrs: {
        root: {
          'data-tooltip': options?.tooltip,
          'data-flowchart-element-type': `action`,
        },
        body: {
          class: `element--action__body ${ElementClasses.ElementBody}`,
          rx: FlowchartConfig.actionNode.styles.borderRadius,
          ry: FlowchartConfig.actionNode.styles.borderRadius,
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-fill) !important` : undefined,
          stroke: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-stroke) !important` : undefined,
          'data-testid': `flowchart-element_body`,
        },
        label: {
          class: `element--action__text ${ElementClasses.ElementText}`,
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-text) !important` : undefined,
          'data-testid': `flowchart-element_label`,
          ...FontAttributes,
          text: nodeText,
          textWrap: {
            width: -10,
            ellipsis: true,
          },
          textVerticalAnchor: 'middle',
        },
      },
    });

    action.on('change:size', (_element: joint.dia.Element, _newSize: joint.dia.Size, _opt) => {
      const updatedSize = this.sizeService.getSizeToGrid(_newSize);
      _element.resize(updatedSize.width, updatedSize.height);
    });

    action.set('id', options?.id ?? action.id);
    action.attr('body/unique-id', options?.uniqueId ?? action.id);

    PortsService.addPorts(action);

    return action;
  }

  createActionNodeInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
    colorPresetId?: string,
  ): joint.dia.Cell {
    let action;

    const title = this.translocoService.translate(FlowchartConfig.defaultTitleKey);

    if (overwrittenPosition) {
      action = this.createActionNode(FlowchartConfig.defaultTitleKey, {
        tooltip: FlowchartConfig.defaultTitleKey,
        x: overwrittenPosition.x,
        y: overwrittenPosition.y,
        colorPresetId,
      });
    } else {
      const actionPosition = this.positionService.calculateStepPosition(
        sourceElement.model.attributes.size,
        sourceElement.model.attributes.position,
        sourceAnchor,
        FlowchartConfig.nodeSize.default,
      );

      action = this.createActionNode(title, {
        x: actionPosition.x,
        y: actionPosition.y,
        tooltip: title,
        colorPresetId,
      });
    }

    return action;
  }
}
